"use client"

import React, { useState } from "react"
import { useRouter } from 'next/navigation';


import { useMutation, useConvex } from 'convex/react';
import { api } from '@/convex/_generated/api';

import useProjectStore from "@/store/projectStore";
import useUserStore from "@/store/userStore";

import Step1Video from "./_components/Step1-Video"
import Step2Audio from "./_components/Step2-Audio"
import Step3Confirm from "./_components/Step3-Confirm"

import ErrorBoundary from "@/components/ErrorBoundary"

import { EndPoints } from "@/app/enum"
import {
    captureReplayWorthyError,
    markPaymentIntentForReplay
} from "@/utils/sentryReplay";

import { Check, ChevronLeft, ChevronRight, Loader2Icon, SparklesIcon } from "lucide-react"


function CreateNewProject() {

    const [loading, setLoading] = useState(false)
    const [currentStep, setCurrentStep] = useState(1); // 记录当前用户执行到的步骤

    const { aimodel, ailoopmode, creditsNeeded, isPublic, projVideo, projAudio, resetProjectStore } = useProjectStore();
    const { userCredits, setUserCredits, userEmail } = useUserStore();

    const [formData, setFormData] = useState() // 用来记录当前的数据

    const router = useRouter();

    const onHandleInputChange = (fieldName, fieldValue) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: fieldValue
        }))

        console.log(formData)
    }

    const GenerateLipSync = async () => {
        setLoading(true);

        // FOR PROD
        // 如果没有视频或音频文件，则返回
        if (!formData?.video || !formData?.audio) {
            setLoading(false);
            alert("Please upload video and audio file!");
            return;
        }

        if (creditsNeeded > userCredits) {
            setLoading(false);
            markPaymentIntentForReplay("insufficient_credits_generate", {
                creditsNeeded,
                userCredits
            });
            alert("You don't have enough credits!");
            return;
        }

        try {
            // 1. 首先上传文件到 R2
            const uploadVideo = async () => {
                const presignedUrl = await fetch('/api/get-upload-url', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        fileName: formData.video.name,
                        fileType: "video",
                        userEmail: userEmail
                    })
                }).then(res => res.json());

                // 使用预签名URL上传文件
                const putRes = await fetch(presignedUrl.url, {
                    method: 'PUT',
                    body: formData.video,
                    headers: {
                        'Content-Type': formData.video.type || 'application/octet-stream',
                    }
                })

                if (!putRes.ok) {
                    const text = await putRes.text().catch(() => '');
                    throw new Error(`上传失败: ${putRes.status} ${text}`);
                }

                return presignedUrl.publicUrl; // 返回可访问的URL
            }

            const uploadAudio = async () => {
                const presignedUrl = await fetch('/api/get-upload-url', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        fileName: formData.audio.name,
                        fileType: "audio",
                        userEmail: userEmail
                    })
                }).then(res => res.json());

                await fetch(presignedUrl.url, {
                    method: 'PUT',
                    body: formData.audio,
                    headers: {
                        'Content-Type': formData.audio.type
                    }
                });

                return presignedUrl.publicUrl;
            }

            // 并行上传文件
            const [videoUrl, audioUrl] = await Promise.all([
                uploadVideo(),
                uploadAudio()
            ])
            console.log('videoUrl:', videoUrl)
            console.log('audioUrl:', audioUrl)

            // FOR TEST FAIL LatentSync
            // const videoUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-11-02/overtimepawshop@gmail.com/video-1486e739.mov" // 蓝色T恤白人男 44 秒视频
            // const audioUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-10-31/emre@novasma.com/audio-bf7fca4b.mp3" // 4 秒音频
            // const audioUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-11-02/overtimepawshop@gmail.com/audio-5f364b76.mp3" // 44 秒音频

            // FOR TEST SUCCESS
            // const videoUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-10-29/dreiky@redesnack.com.br/video-554d95f7.mp4"
            // const audioUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-10-29/dreiky@redesnack.com.br/audio-cef29e11.mp3"

            // FOR TEST
            // const videoUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-11-04/fangye1108@gmail.com/video-48e82adf.mp4" // 实验室白人女 1280*720 8秒视频
            // const audioUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-11-04/fangye1108@gmail.com/audio-c4b99252.wav" // 39 秒音频

            // FOR TEST FAIL Kling
            // const videoUrl = "https://rs.magicface.co/indiesites/lipsync-show/projects/2025-10-31/emre@novasma.com/video-ef7c2bc5.mp4" // 绿色背景西装白人 10 秒视频 2176*928（超出可灵范围）



            // 2. 发送URLs到后端API
            const response = await fetch(EndPoints.POST_TASK, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: projVideo.name,
                    videoUrl,
                    audioUrl,
                    audioType: projAudio.type || "upload",
                    videoDuration: projVideo.duration,
                    audioDuration: projAudio.duration,
                    aimodel,
                    ailoopmode,
                    creditsNeeded,
                    isPublic
                }),
            })

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();
            console.log('Success:', result);
        } catch (error) {
            console.error('Error:', error);
            captureReplayWorthyError(error, "generate_lipsync_failed", {
                creditsNeeded,
                aimodel,
                ailoopmode
            });
        } finally {
            router.replace('/projects')
        }
    }

    return (
        <ErrorBoundary redirectPath="/projects">
        <div className="w-full relative py-4">

            <div className="px-8 mb-8">
                <h1 className='text-2xl font-bold'>Create New LipSync</h1>
                <div className="text-sm text-warning/65">👋 Credits will not be charged if the lipsync task failed.</div>
            </div>

            <div className="flex justify-between items-start">
                <div className="w-[180px] hidden md:block ml-8">
                    <ul className="steps steps-vertical">
                        <li className={`step text-sm step-primary ${currentStep == 1 ? "step-primary" : "step-neutral"} cursor-pointer`} onClick={() => setCurrentStep(1)}>
                            <span className="step-icon font-bold">1</span>Upload Video
                        </li>
                        <li className={`step text-sm step-neutral ${currentStep >= 2 ? "step-primary" : "step-neutral"} cursor-pointer`} onClick={() => setCurrentStep(2)}>
                            <span className="step-icon font-bold">2</span>Choose Audio
                        </li>
                        <li className={`step text-sm ${currentStep == 3 ? "step-primary" : "step-neutral"} cursor-pointer`} onClick={() => setCurrentStep(3)}>
                            <span className="step-icon font-bold"><Check size={16} strokeWidth={4} /></span>Choose Model
                        </li>
                    </ul>
                </div>

                <div
                    className='flex-1 overflow-auto pb-20 px-8'
                    style={{
                        height: 'calc(100vh - 180px)',
                        scrollbarColor: '#fff transparent',
                        scrollbarWidth: 'thin'
                    }}
                >
                    <style jsx>{`div::-webkit-scrollbar {background: transparent !important;}`}</style>

                    <div className='' >
                        {currentStep == 1 && <Step1Video onHandleInputChange={onHandleInputChange} />}
                        {currentStep == 2 && <Step2Audio onHandleInputChange={onHandleInputChange} />}
                        {currentStep == 3 && <Step3Confirm />}
                    </div>
                </div>
            </div>


            <div className="absolute bottom-0 w-full h-[100px] flex justify-end items-center gap-4 px-8" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(10px)' }}>
                {
                    currentStep > 1 && (
                        <button className="btn btn-lg text-base btn-ghost"
                            disabled={loading}
                            onClick={() => setCurrentStep(currentStep - 1)}
                        >
                            <ChevronLeft size={16} strokeWidth={4} />Back
                        </button>
                    )
                }
                {
                    currentStep < 3 && (
                        <button className="btn btn-lg text-base btn-primary w-40"
                            disabled={loading}
                            onClick={() => setCurrentStep(currentStep + 1)}
                        >
                            Next <ChevronRight size={16} strokeWidth={4} />
                        </button>
                    )
                }
                {
                    currentStep == 3 && (
                        <button className="btn btn-lg text-base btn-warning"
                            disabled={loading}
                            onClick={GenerateLipSync}
                        >
                            {/* 使用 CSS 切换显示状态，避免 DOM 节点替换导致的浏览器扩展冲突 */}
                            <div key="loading" className={`items-center gap-2 ${loading ? 'flex' : 'hidden'}`}>
                                <Loader2Icon className='animate-spin' />
                                <span>Uploading & Processing...</span>
                            </div>
                            <div key="generate" className={`items-center gap-2 ${loading ? 'hidden' : 'flex'}`}>
                                <SparklesIcon />
                                <span>Generate LipSync</span>
                            </div>
                        </button>
                    )
                }
            </div>
        </div>
        </ErrorBoundary>
    )
}

export default CreateNewProject
